/**
 * PulseSourcesSection — per-bet Pulse source attach UI.
 *
 * Drops into MarketDetailModal as a `.detail-section`. The betId is taken
 * from the surrounding market context — admin does NOT type a UUID here.
 *
 * Calls the same backend endpoint as PulseSettingsCard:
 *   POST /api/v1/admin/bets/:id/sources   (body: { url })
 *
 * Roles required by backend: master | content | compliance.
 * X bearer + AWS Secrets Manager wiring is identical to PulseSettingsCard.
 */

import React, { useState } from 'react';
import { apiService } from '../services/apiService';
import './PulseSourcesSection.css';

interface PulseSourcesSectionProps {
  betId: string;
}

interface IngestedResult {
  provider: 'X' | 'REDDIT';
  external_id: string;
  external_url: string;
  author_handle: string;
  status: 'new' | 'updated';
}

const PulseSourcesSection: React.FC<PulseSourcesSectionProps> = ({ betId }) => {
  const [url, setUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<IngestedResult | null>(null);

  const trimmedUrl = url.trim();
  const urlValid =
    trimmedUrl.length >= 5 &&
    (trimmedUrl.includes('x.com/') ||
      trimmedUrl.includes('twitter.com/') ||
      trimmedUrl.includes('reddit.com/'));

  const canSubmit = urlValid && !submitting;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    setLastResult(null);
    try {
      const res = await apiService.attachPulseSource(betId, trimmedUrl);
      setLastResult(res.ingested);
      setUrl('');
    } catch (err) {
      // Surface backend error message if the API returned one
      const fallback = err instanceof Error ? err.message : 'Attach failed';
      // axios errors wrap response data on err.response.data — apiService re-throws Error
      // with the same .response when available; pull out the backend message if present.
      const e = err as { response?: { data?: { message?: string; error?: string } } };
      const backend = e?.response?.data?.message || e?.response?.data?.error;
      setError(backend || fallback);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="detail-section pulse-sources-section">
      <div className="detail-row pulse-sources-row">
        <span className="detail-label">📡 Pulse Sources</span>
        <div className="detail-value">
          <p className="pulse-sources-intro">
            Attach a tweet (X / Twitter) or reddit post URL to this market. The backend
            fetches the live post via the platform's official API and persists it; the
            mobile Pulse panel then renders it under this bet.
          </p>

          {error && <div className="pulse-sources-error">{error}</div>}

          {lastResult && (
            <div className="pulse-sources-success">
              <div className="pulse-sources-success-title">
                ✓ {lastResult.status === 'new' ? 'Attached' : 'Updated'}
                {' · '}
                <span className="pulse-sources-provider">{lastResult.provider}</span>
              </div>
              <div className="pulse-sources-success-meta">
                <span>@{lastResult.author_handle}</span>
                {' · '}
                <a
                  href={lastResult.external_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="pulse-sources-link"
                >
                  {lastResult.external_id}
                </a>
              </div>
            </div>
          )}

          <form onSubmit={onSubmit} className="pulse-sources-form">
            <input
              type="url"
              className="form-input pulse-sources-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://x.com/AP/status/2053453751239967085"
              autoComplete="off"
              spellCheck={false}
              disabled={submitting}
            />
            <button
              type="submit"
              className="btn btn-primary pulse-sources-submit"
              disabled={!canSubmit}
            >
              {submitting ? 'Attaching…' : 'Attach Source'}
            </button>
          </form>
          {trimmedUrl.length > 0 && !urlValid && (
            <small className="pulse-sources-hint">
              URL must contain x.com/, twitter.com/, or reddit.com/.
            </small>
          )}
        </div>
      </div>
    </div>
  );
};

export default PulseSourcesSection;
